import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi, useLive, useNow } from '../hooks';
import { post } from '../api';
import { CHANNEL, CHANNEL_NAME, fmtDateTime, intentLabel, timeAgo, who, words } from '../format';
import { Avatar, Badge, Button, Empty, ErrorNote, Loading, Toggle, WindowMeter, useAction } from '../components/ui';
import { I } from '../components/icons';
import { canWrite, useAuth } from '../auth';

type Filter = 'open' | 'bot' | 'human' | 'urgent' | 'resolved' | 'all';
const FILTERS: { id: Filter; label: string }[] = [{ id: 'open', label: 'Open' }, { id: 'human', label: 'With a teammate' }, { id: 'bot', label: 'Bot handling' }, { id: 'urgent', label: 'Urgent' }, { id: 'resolved', label: 'Resolved' }, { id: 'all', label: 'All' }];

export default function Inbox() {
  const { id } = useParams();
  const [filter, setFilter] = useState<Filter>('open');
  const [channel, setChannel] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filter === 'open') p.set('status', 'open');
    if (filter === 'resolved') p.set('status', 'resolved');
    if (filter === 'all') p.set('status', 'all');
    if (filter === 'bot' || filter === 'human') { p.set('mode', filter); p.set('status', 'open'); }
    if (filter === 'urgent') { p.set('priority', 'urgent'); p.set('status', 'open'); }
    if (channel) p.set('channel', channel);
    if (debounced) p.set('q', debounced);
    return p.toString();
  }, [filter, channel, debounced]);
  const list = useApi<any[]>(`/conversations?${qs}`, [qs]);
  useLive((e) => { if (e.type === 'message.created' || e.type.startsWith('conversation') || e.type.startsWith('approval')) list.reload(); });
  const now = useNow(30000);

  return (
    <div className={`inbox ${id ? 'has-thread' : ''}`}>
      <section className="pane list-pane" aria-label="Conversations">
        <div className="pane-head">
          <div className="row"><h1 style={{ fontSize: 'var(--text-lg)' }}>Inbox</h1><span className="spacer" />
            <select className="select" style={{ width: 'auto', minHeight: 30 }} value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Channel">
              <option value="">All channels</option><option value="instagram">Instagram</option><option value="facebook">Facebook</option><option value="web">Website chat</option><option value="email">Email</option>
            </select></div>
          <div className="row" style={{ position: 'relative' }}>
            <I.Search style={{ position: 'absolute', left: 10, color: 'var(--color-muted)' }} />
            <input className="input" style={{ paddingLeft: 34 }} placeholder="Search names, handles, messages" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search conversations" />
          </div>
          <div className="filters" role="group" aria-label="Filter">{FILTERS.map((f) => <button key={f.id} className="filter" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</button>)}</div>
        </div>
        <div className="pane-body">
          {list.loading && !list.data ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
            : list.error ? <div style={{ padding: 16 }}><ErrorNote message={list.error} onRetry={list.reload} /></div>
            : !list.data?.length ? <div style={{ padding: 16 }}><Empty title="Nothing here">{filter === 'human' ? 'No threads are waiting on a teammate.' : 'New Instagram, Facebook, website, and email conversations land here.'}</Empty></div>
            : list.data.map((c) => (
              <Link key={c.id} to={`/inbox/${c.id}`} className={`conv ${c.id === id ? 'active' : ''}`}>
                <Avatar name={who(c)} />
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ flexWrap: 'nowrap' }}><span className="conv-name">{who(c)}</span><span className="chan">{CHANNEL[c.channel]}</span></div>
                  <div className="conv-preview">{c.last_author === 'bot' ? 'Bot: ' : c.last_author === 'human' ? 'You: ' : ''}{c.last_message ?? 'No messages yet'}</div>
                  <div className="row" style={{ marginTop: 6 }}>
                    {c.priority === 'urgent' && <Badge tone="danger">Urgent</Badge>}
                    {c.mode === 'human' ? <Badge tone="accent">Teammate</Badge> : c.status === 'resolved' ? <Badge tone="success">Resolved</Badge> : <Badge tone="brand">Bot</Badge>}
                    {c.pending_approvals > 0 && <Badge tone="warning">Draft waiting</Badge>}
                    {c.intent && <span className="xs muted">{intentLabel(c.intent)}</span>}
                  </div>
                </div>
                <div className="conv-meta"><span>{timeAgo(c.updated_at, now)}</span>{c.window_expires_at && c.status !== 'resolved' && <WindowMeter expiresAt={c.window_expires_at} now={now} mini />}</div>
              </Link>
            ))}
        </div>
      </section>
      <section className="pane thread-pane" aria-label="Conversation">
        {id ? <Thread id={id} onChanged={list.reload} now={now} /> : <div style={{ padding: 32 }}><Empty title="Pick a conversation">Threads with a teammate badge are waiting on a person. The ruler shows how long is left in Meta's 24-hour reply window.</Empty></div>}
      </section>
    </div>
  );
}

function Thread({ id, onChanged, now }: { id: string; onChanged: () => void; now: number }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const d = useApi<any>(`/conversations/${id}`, [id]);
  const ctx = useApi<any>(d.data?.contact?.id ? `/contacts/${d.data.contact.id}/context` : null, [d.data?.contact?.id]);
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [showCtx, setShowCtx] = useState(true);
  const { busy, run } = useAction();
  const bodyRef = useRef<HTMLDivElement>(null);
  useLive((e) => { if (e.conversationId === id) d.reload(); });
  useEffect(() => { bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }); }, [d.data?.messages?.length]);

  if (d.loading && !d.data) return <div style={{ padding: 24 }}><Loading rows={5} /></div>;
  if (d.error) return <div style={{ padding: 24 }}><ErrorNote message={d.error} onRetry={d.reload} /></div>;
  if (!d.data) return null;
  const { conversation: c, contact, messages, approvals } = d.data;
  const writable = canWrite(user);
  const pending = (approvals ?? []).filter((a: any) => a.status === 'pending');
  const act = async (key: string, path: string, ok: string, body: unknown = {}) => { await run(key, () => post(path, body), ok); d.reload(); onChanged(); };
  const send = async () => {
    if (!text.trim()) return;
    const r = await run('send', () => post(`/conversations/${id}/${internal ? 'note' : 'reply'}`, { text }), internal ? 'Note added' : 'Sent');
    if (r) setText('');
    d.reload(); onChanged();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: showCtx ? 'minmax(0,1fr) 300px' : '1fr', height: '100%' }}>
      <div className="thread">
        <div className="thread-head">
          <Button variant="ghost" size="sm" onClick={() => nav('/inbox')} aria-label="Back to list" className="back-btn"><I.Back /></Button>
          <Avatar name={who(contact ?? {})} />
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 'var(--text-base)' }}>{who(contact ?? {})}</h2>
            <div className="row xs muted"><span>{CHANNEL_NAME[c.channel]}</span>{c.intent && <span>{intentLabel(c.intent)}</span>}{contact?.minor_flag && <Badge tone="danger">Under 13</Badge>}{contact?.muted && <Badge tone="neutral">Muted</Badge>}</div>
          </div>
          <span className="spacer" />
          {c.window_expires_at && c.status !== 'resolved' && <WindowMeter expiresAt={c.window_expires_at} now={now} />}
          {writable && (c.mode === 'human'
            ? <Button size="sm" loading={busy === 'mode'} onClick={() => act('mode', `/conversations/${id}/release`, 'Bot resumed on this thread')}><I.Bot />Hand back to bot</Button>
            : <Button size="sm" variant="accent" loading={busy === 'mode'} onClick={() => act('mode', `/conversations/${id}/takeover`, 'You own this thread. The bot is silent.')}><I.Person />Take over</Button>)}
          {writable && (c.status === 'resolved'
            ? <Button size="sm" loading={busy === 'status'} onClick={() => act('status', `/conversations/${id}/reopen`, 'Reopened')}>Reopen</Button>
            : <Button size="sm" loading={busy === 'status'} onClick={() => act('status', `/conversations/${id}/resolve`, 'Resolved')}><I.Check />Resolve</Button>)}
          <Button size="sm" variant="ghost" onClick={() => setShowCtx((v) => !v)} aria-pressed={showCtx}>Customer</Button>
        </div>
        <div className="thread-body" ref={bodyRef} aria-live="polite">
          {messages.map((m: any) => <MessageRow key={m.id} m={m} />)}
          {pending.map((a: any) => <PendingDraft key={a.id} a={a} onDone={() => { d.reload(); onChanged(); }} />)}
        </div>
        {writable && (
          <div className={`composer ${internal ? 'internal' : ''}`}>
            {c.mode === 'bot' && !internal && <span className="xs muted">Sending a reply takes this thread over. The bot stays silent until you hand it back.</span>}
            <textarea className="textarea" placeholder={internal ? 'Internal note for the team. The customer never sees this.' : `Reply on ${CHANNEL_NAME[c.channel]}`} value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send(); }} aria-label="Message" />
            <div className="row">
              <Toggle label="Internal note" checked={internal} onChange={setInternal} />
              <span className="spacer" /><span className="xs muted">Ctrl or Cmd + Enter</span>
              <Button variant="primary" loading={busy === 'send'} disabled={!text.trim()} onClick={send}><I.Send />{internal ? 'Add note' : 'Send as teammate'}</Button>
            </div>
          </div>
        )}
      </div>
      {showCtx && <ContextPanel ctx={ctx} contact={contact} onMute={async (m: boolean) => { await run('mute', () => post(`/contacts/${contact.id}/${m ? 'mute' : 'unmute'}`), m ? 'Customer muted. The bot will not reply to them.' : 'Customer unmuted'); d.reload(); }} writable={writable} />}
    </div>
  );
}

function MessageRow({ m }: { m: any }) {
  const [open, setOpen] = useState(false);
  if (m.direction === 'internal') return <div className={`sys ${m.author === 'human' ? 'note-internal' : ''}`}>{m.author === 'human' ? 'Note: ' : ''}{m.body}</div>;
  const t = m.trace ?? {};
  return (
    <div className={`msg ${m.author} ${m.status === 'failed' ? 'failed' : ''}`}>
      <div className="bubble">{m.body}</div>
      <div className="msg-meta">
        <span>{m.author === 'customer' ? 'Customer' : m.author === 'bot' ? 'Assistant' : 'Teammate'}</span>
        <span>{fmtDateTime(m.created_at)}</span>
        {m.status === 'dry_run' && <Badge tone="info" title="Sandbox: recorded, not sent">Sandbox</Badge>}
        {m.status === 'draft' && <Badge tone="warning">Draft in Help Scout</Badge>}
        {m.status === 'failed' && <Badge tone="danger" title={m.error}>Not delivered</Badge>}
        {t.approval_id && <Badge tone="success">Approved</Badge>}
        {m.author === 'bot' && (t.intent || t.tools) && <button className="linkbtn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide reasoning' : 'Why this reply'}</button>}
      </div>
      {m.status === 'failed' && m.error && <span className="xs" style={{ color: 'var(--color-danger)' }}>{m.error}</span>}
      {open && (
        <div className="trace"><dl>
          <dt>Intent</dt><dd>{intentLabel(t.intent)} {t.classification?.confidence ? `(${Math.round(t.classification.confidence * 100)}%)` : ''}</dd>
          <dt>Method</dt><dd>{t.method === 'llm' ? `Claude (${t.model})` : t.method === 'rules' ? 'Rules (no API key)' : t.flow ? words(t.flow) : 'Automation'}</dd>
          {t.kb?.length > 0 && <><dt>Sources</dt><dd>{t.kb.map((k: any) => k.title).join(', ')}</dd></>}
          {t.tools?.length > 0 && <><dt>Tools</dt><dd>{t.tools.map((x: any) => `${words(x.name)}${x.isError ? ' (failed)' : ''}`).join(', ')}</dd></>}
          {t.guardrail && <><dt>Guardrail</dt><dd>{t.guardrail.pass ? `Passed ${t.guardrail.checks?.length ?? ''} checks` : t.guardrail.issues?.map((i: any) => i.detail).join('; ')}</dd></>}
        </dl></div>
      )}
    </div>
  );
}

function PendingDraft({ a, onDone }: { a: any; onDone: () => void }) {
  const [text, setText] = useState(a.draft);
  const { busy, run } = useAction();
  return (
    <div className="card" style={{ alignSelf: 'flex-end', maxWidth: 560, borderColor: 'var(--color-accent)' }}>
      <div className="card-head"><Badge tone="warning">Draft waiting for approval</Badge><span className="xs muted">{a.reason}</span></div>
      <textarea className="textarea" value={text} onChange={(e) => setText(e.target.value)} aria-label="Draft reply" />
      <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
        <Button size="sm" variant="danger" loading={busy === 'r'} onClick={async () => { await run('r', () => post(`/approvals/${a.id}/reject`), 'Draft rejected'); onDone(); }}>Reject</Button>
        <Button size="sm" variant="primary" loading={busy === 'a'} onClick={async () => { await run('a', () => post(`/approvals/${a.id}/approve`, { text }), 'Approved and sent'); onDone(); }}>Approve and send</Button>
      </div>
    </div>
  );
}

function ContextPanel({ ctx, contact, onMute, writable }: { ctx: any; contact: any; onMute: (m: boolean) => void; writable: boolean }) {
  const c = ctx.data;
  const ok = (x: any) => (x?.ok ? x.data : null);
  return (
    <aside className="pane ctx-pane" aria-label="Customer context" style={{ overflowY: 'auto', borderLeft: '1px solid var(--color-line)' }}>
      <div className="ctx">
        <div className="ctx-section">
          <h3>Customer</h3>
          <dl className="kv">
            <dt>Name</dt><dd>{contact?.display_name ?? 'Unknown'}</dd>
            {contact?.ig_username && <><dt>Instagram</dt><dd>@{contact.ig_username}</dd></>}
            <dt>Email</dt><dd>{contact?.email ?? <span className="muted">Not verified yet</span>}</dd>
          </dl>
          {writable && contact && <Toggle label="Mute the bot for this customer" checked={!!contact.muted} onChange={onMute} />}
        </div>
        {ctx.loading && !c ? <Loading rows={2} /> : ctx.error ? <ErrorNote message={ctx.error} onRetry={ctx.reload} /> : c && (
          <>
            {c.note && <div className="note info">{c.note}</div>}
            {ok(c.shopify) && <div className="ctx-section"><h3>Shopify</h3><dl className="kv"><dt>Orders</dt><dd>{ok(c.shopify).numberOfOrders}</dd><dt>Spent</dt><dd>{ok(c.shopify).amountSpent}</dd></dl></div>}
            {c.orders && <div className="ctx-section"><h3>Recent orders</h3>{c.orders.ok ? (c.orders.data.length ? c.orders.data.map((o: any) => (
              <div key={o.id} className="small"><strong>{o.name}</strong> <span className="muted">{words(String(o.fulfillmentStatus).toLowerCase())}</span>{o.shipments?.[0]?.trackingUrl && <> <a href={o.shipments[0].trackingUrl} target="_blank" rel="noreferrer">Tracking</a></>}</div>)) : <span className="small muted">No orders</span>) : <ErrorNote message={c.orders.error} />}</div>}
            {c.subscriptions && <div className="ctx-section"><h3>Subscriptions</h3>{c.subscriptions.ok ? (c.subscriptions.data.length ? c.subscriptions.data.map((s: any) => (
              <div key={s.id} className="small"><Badge tone={s.status === 'ACTIVE' ? 'success' : 'neutral'}>{words(s.status.toLowerCase())}</Badge> {s.items.map((i: any) => i.title).join(', ')}<div className="xs muted">Next order {s.nextBillingDate} ({s.frequency})</div></div>)) : <span className="small muted">None</span>) : <ErrorNote message={c.subscriptions.error} />}</div>}
            {c.klaviyo && <div className="ctx-section"><h3>Klaviyo</h3><span className="small">{c.klaviyo.ok ? (c.klaviyo.data?.subscribed ? 'Subscribed to email' : 'Not subscribed') : c.klaviyo.error}</span></div>}
            {c.conversations?.length > 1 && <div className="ctx-section"><h3>Other conversations</h3>{c.conversations.slice(1).map((x: any) => <Link key={x.id} className="small" to={`/inbox/${x.id}`}>{CHANNEL_NAME[x.channel]}: {intentLabel(x.intent)} ({x.status})</Link>)}</div>}
          </>
        )}
      </div>
    </aside>
  );
}
